import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  buttonClasses,
  Card,
  cn,
  Field,
  linkTextClasses,
  Page,
  PageHeader,
  Pagination,
  Select,
  showToast,
  Table,
  TextArea,
  Toast,
  type Action,
  type ComboboxOption,
} from '@evogard/ui';
import { loadFailureMessage, type TicketAct } from '@evogard/domain';
import type { TicketSummary } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  Deadline,
  TicketStatusBadge,
  count,
  formatDate,
  refusalFor,
  useIsConsultant,
  viewerFrom,
} from './common.js';
import {
  TicketFilterButton,
  TicketMineToggle,
  ticketSearch,
  useTicketFilters,
} from './filters.js';
import { TransferForm } from './TransferForm.js';
import { StatusForm } from './StatusForm.js';
import { RequesterPicker, VehiclePicker } from './OpeningPickers.js';

/**
 * A fila de demandas da base atual.
 *
 * Duas coisas que a tela faz e que parecem detalhe:
 *
 * - **A `queryKey` carrega o tenant**, e o `placeholderData` só segura o
 *   resultado anterior dentro da mesma base. Trocar de base mantendo a lista
 *   antiga faria o cabeçalho nomear a base nova sobre os dados da anterior —
 *   coberto por `e2e/internal.tenant.spec.ts`.
 * - **Carregando é esqueleto, não lista vazia.** "Nenhuma demanda" enquanto a
 *   consulta está no ar é a tela afirmando um fato que o código ainda não sabe.
 */
export function TicketsScreen() {
  const { data: context } = useSessionContext();
  const [opening, setOpening] = useState(false);
  /*
   * Os recortes moram em `filters.tsx`, e não aqui: o relatório usa os MESMOS,
   * e a verificação da `demandas-42` é que os dois contem igual. Dois blocos de
   * sete seletores copiados divergem no primeiro filtro novo — e a divergência
   * aparece como um relatório que discorda da lista, sem nada dizendo por quê.
   */
  const filters = useTicketFilters();

  /*
   * Até 15/09 a lista vinha com `page: 1, perPage: 50` e o aviso de corte
   * embaixo do título. O aviso existia porque não havia como virar página.
   */
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const tenantId = context?.currentTenant.id;
  const canCreate = can(context, 'tickets.tickets', 'criar');
  const canSeeCategories = can(context, 'tickets.categories', 'ver');
  const canAssign = can(context, 'tickets.tickets', 'atribuir');
  /* concluir, cancelar e mover entre as situações abertas — o piso da rota */
  /* passar a demanda adiante — a outro setor ou a um colaborador dele */
  const canTransfer = can(context, 'tickets.tickets', 'encaminhar');
  const canChangeStatus = can(context, 'tickets.tickets', 'concluir');

  /* quem está olhando — os setores vêm do contexto da sessão desde 17/09 */
  /* o consultor só abre e acompanha: sem a barra de recortes, e a mesa é a dele */
  const isConsultant = useIsConsultant();
  const viewer = viewerFrom(context, isConsultant);
  const myId = context?.user.id;
  const navigate = useNavigate();
  const client = useQueryClient();
  const [claimError, setClaimError] = useState<string | null>(null);

  /**
   * Aderir, a partir do painel de ações.
   *
   * Ela subiu de `ClaimIconButton` para cá em 17/09 porque a ação deixou de ter
   * um botão por linha: o painel fecha ao rodar, e uma mutação que vive no
   * botão desmontaria junto com ele — levando o `isPending` e o erro embora
   * antes de a resposta chegar.
   *
   * **O clique não confia no que está na tela.** A mesa não se atualiza
   * sozinha, então a linha pode estar velha; a dupla trava do `where` de
   * `claim` no servidor é quem decide, e o que ela recusar aparece no aviso
   * acima da tabela — o painel já fechou, e um erro dentro dele seria um erro
   * que ninguém lê.
   */
  const claim = useMutation({
    mutationFn: async (ticket: TicketSummary) => {
      const r = await api.tickets.claim({ params: { id: ticket.id }, body: {} });
      return ok(r, 200, 'Não foi possível aderir.');
    },
    onMutate: () => setClaimError(null),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['demandas'] }),
    onError: (e: Error) => setClaimError(e.message),
  });

  /**
   * Copiar o link de acompanhamento.
   *
   * ## Por que a URL vem do servidor, e não daqui
   *
   * A interna **não sabe onde o portal mora** — não há constante nem variável de
   * ambiente com aquele host em `apps/web`, e é assim de propósito: são duas
   * aplicações, cada uma conhecendo o próprio endereço. Montar o link no
   * navegador exigiria uma segunda verdade sobre isso, que envelheceria no dia
   * em que o domínio do portal mudasse — e o sintoma seria um link que só quebra
   * na mão de quem recebeu.
   *
   * A rota também não devolve o token: devolve o endereço pronto. O token não
   * tem uso nenhum nesta tela além de virar URL.
   *
   * ## Por que o aviso é `toast` e não uma linha no painel
   *
   * O `ActionsModal` **fecha antes de rodar** e não tem área de estado — o que é
   * a decisão certa dele, porque "o progresso aparece onde o resultado aparece".
   * Aqui o resultado não tem lugar na tabela: nada muda na linha. O toast é o
   * canal que a casa já usa para o aviso que ninguém pediu.
   *
   * A falha vai para o mesmo aviso do `claim`, acima da tabela: um 403 daqui
   * significa "você vê a mesa mas não pode ver esta conversa", que é uma frase
   * que a pessoa precisa ler com calma, não um toast que some em oito segundos.
   */
  const copyLink = useMutation({
    mutationFn: async (ticket: TicketSummary) => {
      const r = await api.tickets.trackingLink({ params: { id: ticket.id } });
      const { url } = ok(r, 200, 'Não foi possível gerar o link.');

      /*
       * A cópia acontece DENTRO da mutação, e o sucesso dela é o sucesso da
       * ação. Confirmar "copiado" e depois descobrir que a área de transferência
       * recusou — contexto inseguro, permissão negada — é a tela afirmando o que
       * o navegador não cumpriu, e quem acreditar manda uma mensagem vazia.
       */
      if (!navigator.clipboard) {
        throw new Error(`Seu navegador não deixa copiar por aqui. O link é: ${url}`);
      }
      await navigator.clipboard.writeText(url);
      return ticket;
    },
    onMutate: () => setClaimError(null),
    onSuccess: (ticket) =>
      showToast((dismiss) => (
        <Toast title="Link copiado" icon="copiar" onClose={dismiss}>
          <p className="text-sm">
            Quem receber acompanha o ticket{' '}
            <span className="font-mono">{ticket.code}</span> sem precisar entrar.
          </p>
        </Toast>
      )),
    onError: (e: Error) => setClaimError(e.message),
  });

  /*
   * A lista de categorias continua aqui — não pelo filtro, que a barra busca
   * sozinha, mas pelo formulário de abertura, que precisa do prazo de cada uma.
   *
   * ## Rota própria desde 18/09/2026, e chave própria
   *
   * `listOpeningCategories` devolve só as categorias em que ESTA pessoa pode
   * abrir; `listCategories` continua sendo a lista inteira, que a barra de
   * filtros e a tela de administração usam. As duas chaves de cache tinham de
   * se separar junto: com a mesma, o filtro passaria a esconder categorias que
   * a pessoa legitimamente lê nas demandas — e ninguém entenderia por quê.
   *
   * O `enabled` também trocou de permissão. Era `tickets.categories:ver`, que é
   * a de quem ADMINISTRA categoria; quem só abre demanda não a tem, e o seletor
   * vinha vazio com o formulário dizendo "esta base não tem categoria".
   */
  const categories = useQuery({
    queryKey: ['demandas', 'categorias-abertura', tenantId],
    enabled: canCreate,
    queryFn: async () => {
      const r = await api.tickets.listOpeningCategories();
      return ok(r, 200, 'Não foi possível carregar as categorias.');
    },
  });

  const ticketsQuery = useQuery({
    queryKey: ['demandas', 'lista', tenantId, ...filters.key, page, perPage],
    queryFn: async () => {
      const r = await api.tickets.list({
        query: { page, perPage, ...filters.query },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os tickets'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const total = ticketsQuery.data?.total;

  /*
   * Estreitar o recorte encurta a lista, e a página em que se estava deixa de
   * existir: filtrar por uma situação estando na página 4 pediria um `offset`
   * além do fim, e a resposta seria uma tabela vazia sob um cartão dizendo o
   * total. `perPage` está aqui pelo mesmo motivo, e a praça também.
   */
  useEffect(() => {
    setPage(1);
    /* `filters.key` já É a lista de dependências do recorte — ver `useTicketFilters`. */
  }, [tenantId, perPage, ...filters.key]);

  return (
    <Page measure="cheia">
      <PageHeader
        title="Tickets"
        actions={
          <>
            {canSeeCategories && (
              /*
               * `buttonClasses` e não `<Botao>`: isto navega, então é um link — e
               * um link precisa ser `<a>` para abrir em outra aba, para o teclado
               * andar nele como link e para o leitor de tela anunciar o que é.
               * Um `<button onClick={navegar}>` com cara de link é a troca que o
               * `DESIGN.md` chama de aparência sobre semântica.
               */
              <Link
                to="/tickets/categories"
                className={buttonClasses({ variant: 'contorno' })}
              >
                Categorias
              </Link>
            )}
            {/* o quadro é a mesma mesa por situação, com o MESMO recorte do endereço */}
            <Link
              to="/tickets/board"
              search={(current: Record<string, unknown>) => current}
              className={buttonClasses({ variant: 'contorno' })}
            >
              Ver quadro
            </Link>
            {/* mesmo `tickets.tickets:ver` da mesa: o relatório conta o que ela
                lista, sobre o mesmo recorte */}
            <Link to="/tickets/report" className={buttonClasses({ variant: 'contorno' })}>
              Relatório
            </Link>
            {/* Esconder é cortesia; quem protege é o guard. A rota continua
                exigindo `tickets.tickets:criar`. */}
            {canCreate && (
              <Button icon="mais" onClick={() => setOpening(true)}>
                Abrir ticket
              </Button>
            )}
          </>
        }
      >
        O que está na mesa em {context?.currentTenant.nome}.
      </PageHeader>

      {opening && canCreate && (
        <OpeningForm
          categories={categories.data ?? []}
          loadingCategories={categories.isLoading}
          canSeeCategories={canSeeCategories}
          toClose={() => setOpening(false)}
        />
      )}

      {ticketsQuery.isError && <Alert tone="erro">{ticketsQuery.error.message}</Alert>}

      {claimError && <Alert tone="erro">{claimError}</Alert>}

      {/*
       * Com a consulta em erro, `isLoading` é falso e `itens` é vazio — e a
       * tabela anunciaria "Nenhuma demanda aberta nesta base ainda" logo abaixo
       * do aviso dizendo que não deu para carregar. Duas afirmações contrárias
       * na mesma tela, e a de baixo é a que parece um fato.
       *
       * Erro sem dado nenhum: o aviso é a resposta inteira. Erro depois de já
       * ter carregado (uma refetch que falhou): a lista de antes continua de pé,
       * porque ela é verdadeira — o aviso acima diz que pode estar velha.
       */}
      {(!ticketsQuery.isError || ticketsQuery.data) && (
        <Card
          title={total === undefined ? 'Carregando…' : count(total, 'ticket', 'tickets')}
          bleed
        >
          <Table
            paginate={false}
            /*
             * A busca e o botão de filtros entram na barra da própria tabela —
             * é o desenho de 17/09, e é por isso que o cartão de recortes saiu
             * de cima da tela. O que a barra NÃO guarda é o recorte: ele mora
             * no endereço (`useTicketFilters`), e o painel só o devolve
             * confirmado.
             */
            search={ticketSearch(filters)}
            /*
             * Dois IRMÃOS, e não um `<div>` com os dois dentro.
             *
             * `Toolbar` aplica `*:h-11 *:border-borda` ao filho DIRETO
             * (`packages/ui/src/Toolbar.tsx`): envolvidos, o `div` levaria a
             * altura e a borda, e os dois de dentro ficariam sem nenhuma das
             * duas. Como irmãos, cada um entra na medida da barra sozinho.
             */
            toolbar={
              <>
                {isConsultant === false && <TicketMineToggle filters={filters} />}
                <TicketFilterButton filters={filters} />
              </>
            }
            label="Tickets desta base"
            items={ticketsQuery.data?.items ?? []}
            loading={ticketsQuery.isLoading}
            rowKey={(d) => String(d.id)}
            /*
             * Três vazios, e cada um nomeia a própria causa.
             *
             * "Nenhuma demanda" nos três seria a tela afirmando que não há,
             * quando o que houve foi um recorte — e o pior dos três é o de quem
             * não está em setor nenhum: a mesa em branco parece defeito, e o
             * conserto não está com ela.
             */
            empty={
              viewer && viewer.departmentIds.length === 0 && filters.mine === 'meu_setor'
                ? 'Você não está em nenhum setor, então não há fila sua para mostrar. ' +
                  'Quem define os setores é quem administra a plataforma.'
                : filters.mine === 'meu_setor'
                  ? 'Nenhum ticket no seu setor com esses filtros.'
                  : filters.mine === 'abri'
                    ? 'Nenhum ticket que você abriu ou pediu para outro setor.'
                    : filters.mine === 'comigo'
                      ? 'Nenhum ticket aderido a você com esses filtros.'
                      : isConsultant
                        ? 'Você ainda não abriu nenhum ticket. Os que abrir aparecem aqui.'
                        : filters.active
                          ? 'Nenhum ticket com esses filtros.'
                          : 'Nenhum ticket por aqui ainda. Os que chegarem aparecem nesta mesa.'
            }
            rowTitle={(d) => `Ticket ${d.code}`}
            rowSubtitle={(d) => d.title}
            rowIcon="demandas"
            /*
             * As quatro colunas de ícone da mesa viraram este painel em 17/09 —
             * visualizar, aderir, encaminhar e a seta de abrir.
             *
             * **O resumo rápido saiu junto.** Ele e a seta faziam a mesma
             * pergunta ("o que é esta demanda?") com duas respostas diferentes,
             * e a única razão de o modal existir era não perder o recorte da
             * mesa ao navegar. Desde que o recorte mora no ENDEREÇO
             * (`useTicketFilters`), voltar devolve a mesa inteira — e a resposta
             * completa passou a custar o mesmo que a resumida.
             *
             * O que se ganha além da coluna: as ações que não cabem agora
             * **aparecem com o motivo**. Antes elas simplesmente não eram
             * renderizadas, e nada na tela dizia por que a demanda do vizinho
             * tinha três botões e a sua, um.
             */
            /*
             * Cada ação carrega o MOTIVO de não estar disponível, e o motivo vem
             * do domínio — `refusalToActOnTicket`, a mesma função que o serviço
             * chama antes de responder 403. Não são duas redações do mesmo fato:
             * é a mesma string.
             *
             * Nenhuma ação SOME por papel. Some por permissão (o piso, que a
             * tela de grupos concede) e fica desabilitada com a explicação por
             * papel — "esta demanda é do seu setor e está com outra pessoa".
             * Sumir sem dizer por quê é o que faz a pessoa achar que quebrou.
             */
            rowActions={(d) => {
              const refusal = (act: TicketAct) => refusalFor(act, d, viewer, myId);
              return [
                {
                  label: 'Abrir o ticket',
                  icon: 'olho',
                  /*
                   * A linha abre sempre: a ficha mostra o resumo a quem está no
                   * escopo, e é ela que explica por que a conversa não aparece.
                   * Desabilitar aqui trocaria uma tela que ensina o caminho por
                   * um botão morto.
                   */
                  run: () =>
                    void navigate({ to: '/tickets/$id', params: { id: String(d.id) } }),
                },
                {
                  /*
                   * O link abre a conversa sem login para quem o receber, então
                   * quem pode copiá-lo é exatamente quem pode lê-la. Demanda
                   * fechada não impede: é justamente depois de concluída que
                   * alguém pergunta o que aconteceu.
                   */
                  label: 'Copiar link de acompanhamento',
                  icon: 'copiar',
                  disabled: refusal('copyLink'),
                  run: () => copyLink.mutate(d),
                },
                ...(canAssign
                  ? [
                      {
                        label: 'Aderir a mim',
                        icon: 'confirmado',
                        /*
                         * "Já é de fulano" continua vindo daqui, com o NOME —
                         * o domínio não o conhece, e "esta demanda já está com
                         * alguém" é pior do que dizer com quem.
                         */
                        disabled: d.assignee
                          ? `Já é de ${d.assignee.nome}.`
                          : refusal('claim'),
                        run: () => claim.mutate(d),
                      } satisfies Action,
                    ]
                  : []),
                /*
                 * `encaminhar`, e só ela (23/09/2026). Antes era `atribuir` E
                 * `platform.departments:ver` — a segunda escondia o item de
                 * quem só atende, porque o formulário listava os setores pela
                 * rota da plataforma. Agora a lista é do próprio módulo.
                 */
                ...(canTransfer
                  ? [
                      {
                        label: 'Encaminhar',
                        icon: 'seta-direita',
                        disabled: refusal('transfer'),
                        /*
                         * `form`, e não `run`: encaminhar pergunta setor e
                         * justificativa, e o painel troca de conteúdo no lugar.
                         * Com `run` abrindo um segundo diálogo, seriam dois véus
                         * empilhados — o defeito que o painel existe para não ter.
                         */
                        form: (close: () => void) => (
                          <TransferForm ticket={d} onDone={close} />
                        ),
                      } satisfies Action,
                    ]
                  : []),
                ...(canChangeStatus
                  ? [
                      {
                        label: 'Mudar situação',
                        icon: 'atualizar',
                        /*
                         * Encerrada não vai a lugar nenhum (`TICKET_TRANSITIONS`):
                         * dizer isso é melhor do que abrir um formulário sem
                         * opção nenhuma. O resto é a regra de papel do domínio.
                         */
                        disabled:
                          d.situacao === 'concluida' || d.situacao === 'cancelada'
                            ? `O ticket já está ${d.situacao === 'concluida' ? 'concluído' : 'cancelado'}.`
                            : refusal('close'),
                        form: (close: () => void) => (
                          <StatusForm ticket={d} onDone={close} />
                        ),
                      } satisfies Action,
                    ]
                  : []),
              ];
            }}
            columns={[
              {
                title: 'Código',
                minWidth: '7rem',
                cell: (d) => (
                  <Link
                    to="/tickets/$id"
                    params={{ id: String(d.id) }}
                    className={linkTextClasses(
                      'font-medium tabular-nums pointer-coarse:px-0',
                    )}
                  >
                    {d.code}
                  </Link>
                ),
              },
              {
                title: 'Título',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (d) => (
                  <div className="min-w-0">
                    <p className="truncate">{d.title}</p>
                    <p className="truncate text-xs text-texto-suave">
                      {d.categoria?.nome ?? 'Sem categoria'} ·{' '}
                      {d.onBehalfOf?.nome ?? d.requester.nome}
                      {d.vehicle && (
                        <span className="font-mono">
                          {' '}
                          · {d.vehicle.placa ?? d.vehicle.code}
                        </span>
                      )}
                    </p>
                  </div>
                ),
              },
              {
                title: 'Fila',
                minWidth: '8rem',
                /*
                 * A coluna existe porque o filtro de departamento existe.
                 * Filtrar por uma coluna que a tabela não mostra é a pessoa
                 * escolhendo "Financeiro" e não tendo como conferir que o
                 * resultado é o Financeiro — a lista muda e nada na tela diz
                 * por quê.
                 */
                cell: (d) => (
                  <p className="truncate">
                    {d.department?.nome ?? (
                      <span className="text-texto-suave">Sem departamento</span>
                    )}
                  </p>
                ),
              },
              {
                /*
                 * Coluna própria desde 06/09, e não mais a segunda linha da
                 * "Fila".
                 *
                 * As duas empilhadas cabiam quando a mesa tinha 1024px; com a
                 * largura do desenho elas viraram uma coluna estreita ao lado
                 * de metade da tela vazia. E são perguntas diferentes: a fila é
                 * de que setor a demanda é, o responsável é quem responde por
                 * ela — cada uma com o seu filtro, cada uma ordenável por si.
                 */
                title: 'Responsável',
                minWidth: '10rem',
                cell: (d) => (
                  <span className="flex min-w-0 items-center gap-2">
                    <Avatar nome={d.assignee?.nome ?? null} size="sm" />
                    <span className={cn('truncate', !d.assignee && 'text-texto-suave')}>
                      {d.assignee?.nome ?? 'Sem responsável'}
                    </span>
                  </span>
                ),
              },
              {
                title: 'Prazo',
                cell: (d) => (
                  <Deadline dueAt={d.dueAt} completed={Boolean(d.completedAt)} />
                ),
              },
              {
                title: 'Situação',
                cell: (d) => <TicketStatusBadge situacao={d.situacao} />,
              },
            ]}
          />

          <Pagination
            page={page}
            perPage={perPage}
            total={total ?? 0}
            onChange={setPage}
            onPerPageChange={setPerPage}
            label="tickets"
            /* o cartão é `bleed`; o espaçamento é de quem entra nele */
            className="border-t border-borda-suave px-5 py-4"
          />
        </Card>
      )}
    </Page>
  );
}

/**
 * Abrir demanda.
 *
 * **Sem categoria cadastrada, o formulário diz isso e não deixa abrir.** As
 * categorias e os prazos vêm do aplicativo original (parte 7) e ainda não foram
 * lidos — semear "Financeiro / 5 dias" seria a tela apresentando como fato um
 * número que ninguém verificou, e um job diário cobrando prazo em cima dele.
 */
function OpeningForm({
  categories,
  loadingCategories,
  canSeeCategories,
  toClose,
}: {
  categories: Array<{ id: number; nome: string; deadlineDays: number | null }>;
  loadingCategories: boolean;
  /** a lista de categorias exige `tickets.categories:ver`, que é outra permissão */
  canSeeCategories: boolean;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [title, setTitle] = useState('');
  const [descricao, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [vehicle, setVehicle] = useState<ComboboxOption | null>(null);
  const [requester, setRequester] = useState<ComboboxOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * Quem abre, segundo o servidor: o consultor escolhe entre a carteira dele e
   * é o próprio solicitante. Enquanto a resposta não chega, nenhum dos dois
   * campos aparece — mostrar o de solicitante e tirá-lo em seguida seria a
   * tela oferecendo o que a gravação recusaria.
   */
  const isConsultant = useIsConsultant();
  const consultant = isConsultant === true;

  const create = useMutation({
    mutationFn: async () => {
      const r = await api.tickets.create({
        body: {
          title: title.trim(),
          descricao: descricao.trim(),
          categoryId: categoryId ? Number(categoryId) : null,
          vehicleCode: vehicle?.value ?? null,
          onBehalfOfUserId: consultant ? null : (requester?.value ?? null),
        },
      });
      return ok(r, 201, 'Não foi possível abrir o ticket.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['demandas'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const chosen = categories.find((cat) => String(cat.id) === categoryId);

  return (
    <Card
      title="Abrir ticket"
      descricao="O código de acompanhamento é gerado na abertura."
      actions={
        <Button variant="discreto" size="sm" onClick={toClose}>
          Cancelar
        </Button>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        {/*
         * Duas ausências, duas frases — e a distinção não é preciosismo.
         *
         * `tickets.tickets:criar` e `tickets.categories:ver` são permissões
         * separadas, e quem tem a primeira sem a segunda tinha a lista vazia
         * por 403. A tela então dizia "Nenhuma categoria cadastrada nesta
         * base" sobre uma base com dezenas — afirmando um fato que o código
         * nunca chegou a consultar, e mandando a pessoa cadastrar de novo o
         * que já existe.
         */}
        {!canSeeCategories ? (
          <Alert tone="alerta" title="Você não tem acesso à lista de categorias.">
            Abrir ticket exige escolher uma categoria, e a lista vem da permissão
            “Categorias de ticket: ver”. Peça a quem administra a plataforma.
          </Alert>
        ) : (
          !loadingCategories &&
          categories.length === 0 && (
            <Alert tone="alerta" title="Nenhuma categoria cadastrada nesta base.">
              As categorias e os prazos vêm do sistema atual e ainda não foram
              cadastrados. Cadastre em{' '}
              <Link to="/tickets/categories" className="underline">
                Categorias
              </Link>{' '}
              antes de abrir o primeiro ticket.
            </Alert>
          )
        )}

        <Field
          label="Título"
          required
          maxLength={160}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Em uma linha, o que precisa ser resolvido"
        />

        <Select
          label="Categoria"
          required
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          help={
            chosen
              ? chosen.deadlineDays === null
                ? 'Esta categoria não tem prazo definido.'
                : `Prazo de ${chosen.deadlineDays} dias a partir de hoje.`
              : undefined
          }
        >
          <option value="">Escolha…</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.nome}
            </option>
          ))}
        </Select>

        {isConsultant !== undefined && (
          <div className="grid gap-4 md:grid-cols-2">
            <VehiclePicker
              value={vehicle}
              onChange={setVehicle}
              consultant={consultant}
            />
            {!consultant && <RequesterPicker value={requester} onChange={setRequester} />}
          </div>
        )}

        <TextArea
          label="Descrição"
          required
          maxLength={10_000}
          counter
          value={descricao}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="O que aconteceu, o que já foi tentado e o que se espera."
        />

        <div className="flex gap-2">
          <Button
            type="submit"
            loading={create.isPending}
            disabled={categories.length === 0}
          >
            Abrir
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Reexportado para a tela de categorias não reimportar o mesmo formatador. */
export { formatDate };
